import React, {useCallback, useEffect, useMemo, useState} from "react";
import Dialog from "../../base/dialog";
import QuickOrderImportPartsSection from "./sections/importing-section";
import QuickOrderCustomerSelectionSection from "./sections/customer-selection-section";
import QuickOrderSummarySection from "./sections/summary-section";
import {useDispatch, useSelector} from "react-redux";
import {setReduxDialogState} from "../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../core/models/static/routes/redux-store-routes";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import {useAppSelector} from "../../../redux";
import ToastService from "../../../../core/services/toast";
import {useSuggestiveSelling} from "../../../features/suggestive-selling";

/**
 * Different steps for creating quick orders dialog
 */
export const QuickOrderSteps = {
    ImportSection: 'importSection',
    CustomerSelection: 'customerSelection',
    SummarySection: 'summarySection',
}

/**
 * Different statuses of part number text field in the quick order entities
 */
export const QuickOrderEntityPartNumberFieldStatus = {
    UnTouched: 'unTouched',
    Valid: 'valid',
    InValid: 'inValid',
    Loading: 'loading',
    Duplicate: 'duplicate',
}

const QuickOrderDialog = () => {
    const {clearQuickOrderSuggestiveSuppression} = useSuggestiveSelling();
    const state = useSelector(state => state?.[ReduxStoreRoutes.dialogs.base][ReduxStoreRoutes.dialogs.quickOrders] ?? {});
    const [status, setStatus] = useState(QuickOrderSteps.ImportSection);
    const [importedItems, setImportedItems] = useState(undefined);
    const [createdOrder, setCreatedOrder] = useState(undefined);
    const [warehouses, setWarehouses] = useState([]);
    const {
        getAvailabilityForPartNo,
        validatePartNo,
        getPriceForCustomerAndPartNo,
        invalidateAvailabilityCache,
        invalidateValidityCache,
        invalidatePriceCache,
        getAllPricesForCustomer,
    } = useBatchApi();
    const isMounted = useIsMounted();

    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const [customer, setCustomer] = useState(null);
    const [orderExtraInformation, setOrderExtraInformation] = useState({});
    const dispatch = useDispatch();
    const [dialogWidth, setDialogWidth] = useState(undefined);

    /**
     * Warns the user when the main API is used in the development environment.
     */
    useEffect(() => {
        if (state?.open) {
            ToastService.mainAPIRedZoneWarning();
        }
    }, [state?.open])

    /**
     * Resets the state of this dialog to its initial values.
     */
    const resetState = useCallback(() => {
        clearQuickOrderSuggestiveSuppression();
        setStatus(QuickOrderSteps.ImportSection);
        setImportedItems(undefined);
        setCreatedOrder(undefined);
        invalidateAvailabilityCache();
        invalidateValidityCache();
        invalidatePriceCache();
        setCustomer(null);
        setOrderExtraInformation({});
        setDialogWidth(undefined);
    }, [clearQuickOrderSuggestiveSuppression, invalidateAvailabilityCache, invalidateValidityCache, invalidatePriceCache])

    /**
     * As soon as the component mounts:
     * - sets the selected customer if we are in the customer-based views (customer panel).
     */
    useEffect(() => {
        if (state?.open) {
            if (!!defaultCustomer?.id) {
                setCustomer(defaultCustomer);
            }
        }
        if (!state?.open) {
            resetState();
        }
    }, [defaultCustomer, state?.open, resetState])

    /**
     * Changes the status of the dialog based on the received status
     * @type {(function(*): void)}
     */
    const changeDialogStatus = useCallback((newStatus) => setStatus(newStatus), [])

    /**
     * Closes the current dialog and resets the state back to its initial value.
     */
    const close = useCallback(() => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.quickOrders, {open: false}));
    }, [dispatch])

    /**
     * Gets the sales person's warehouses and access levels.
     * @type {(function(): Promise<void>)}
     */
    const getSalesPersonWarehousesAndAccessLevels = useCallback(async () => {
        const response = await Api.getSalesPersonProfile()
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setWarehouses(response.data?.warehouseAccess ?? []);
        }
    }, [isMounted])

    /**
     * Fetches the sales person's warehouses and access levels with the first render.
     */
    useEffect(() => {
        if (!state?.open) return;
        void getSalesPersonWarehousesAndAccessLevels();
    }, [getSalesPersonWarehousesAndAccessLevels, state?.open])


    const dialogSize = useMemo(() => {
        if (dialogWidth) {
            return dialogWidth;
        }
        switch (status) {
            case QuickOrderSteps.ImportSection:
            case QuickOrderSteps.CustomerSelection:
            default:
                return 'sm';
            case QuickOrderSteps.SummarySection:
                return 'lg';
        }
    }, [dialogWidth, status])

    return (
        <Dialog
            open={state?.open ?? false}
            setOpen={close}
            maxWidth={dialogSize}
            className={'bound-dialog quick-order-dialog'}
            scroll={'paper'}
            aria-labelledby="scroll-dialog-title"
        >
            {
                QuickOrderSteps.ImportSection === status &&
                <QuickOrderImportPartsSection
                    close={close}
                    changeDialogStatus={changeDialogStatus}
                    items={importedItems}
                    customer={customer}
                    warehouses={warehouses}
                    setItems={setImportedItems}
                    getAvailabilityForPartNo={getAvailabilityForPartNo}
                    validatePartNo={validatePartNo}
                    getPriceForCustomerAndPartNo={getPriceForCustomerAndPartNo}
                    setCustomer={setCustomer}
                    setOrderExtraInformation={setOrderExtraInformation}
                    setDialogWidth={setDialogWidth}
                />
            }
            {
                QuickOrderSteps.CustomerSelection === status &&
                <QuickOrderCustomerSelectionSection
                    close={close}
                    items={importedItems}
                    customer={customer}
                    setCustomer={setCustomer}
                    changeDialogStatus={changeDialogStatus}
                />
            }
            {
                QuickOrderSteps.SummarySection === status &&
                <QuickOrderSummarySection
                    order={createdOrder}
                    setOrder={setCreatedOrder}
                    createNewOrder={resetState}
                    changeDialogStatus={changeDialogStatus}
                    items={importedItems}
                    customer={customer}
                    orderExtraInformation={orderExtraInformation}
                    getAllPricesForCustomer={getAllPricesForCustomer}
                />
            }
        </Dialog>
    );
}

export default QuickOrderDialog;


/**
 * Create a deferred promise.
 * @returns {{resolve, reject, promise: Promise<unknown>}}
 */
const createDeferred = () => {
    let resolve, reject;
    const promise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
    });
    return {promise, resolve, reject};
}

const useBatchApi = () => {
    const [validPartNos, setValidPartNos] = useState([]);
    const [invalidPartNos, setInvalidPartNos] = useState([]);
    const [warehouseAvailabilities, setWarehouseAvailabilities] = useState({});
    const [validateQueue, setValidateQueue] = useState({});
    const [availabilityQueue, setAvailabilityQueue] = useState({});
    const [priceCacheQueue, setPriceCacheQueue] = useState({});
    const [pendingValidations, setPendingValidations] = useState([]);
    const [pendingAvailabilities, setPendingAvailabilities] = useState([]);
    const [priceCache, setPriceCache] = useState({});
    const [pendingPrices, setPendingPrices] = useState([]);
    const isMounted = useIsMounted();

    /**
     * Process the validation queue.
     * @type {(function(): Promise<void>)}
     */
    const processValidateQueue = useCallback(async () => {
        const partNumbers = Object.keys(validateQueue).filter((partNo) => !pendingValidations.includes(partNo));
        if (partNumbers.length === 0) return;

        // Add these items to pending validations
        setPendingValidations((prev) => [...prev, ...partNumbers]);

        const response = await Api.validatePartNumbers({partNumbers});

        if (!isMounted()) return;

        if (!response?.resultFlag) {
            // Clear pending items if the response fails
            setPendingValidations((prev) => prev.filter((partNo) => !partNumbers.includes(partNo)));
            return;
        }

        const validPartNos = [];
        const invalidPartNos = [];
        for (const key of Object.keys(response?.data ?? {})) {
            if (response.data?.[key]) {
                validPartNos.push(key);
            } else {
                invalidPartNos.push(key);
            }
        }

        setValidPartNos((prev) => {
            const set = new Set([...prev, ...validPartNos]);
            return Array.from(set);
        });
        setInvalidPartNos((prev) => {
            const set = new Set([...prev, ...invalidPartNos]);
            return Array.from(set);
        });

        // Resolve queued promises
        setValidateQueue((prevQueue) => {
            const updatedQueue = {...prevQueue};
            for (const validPartNo of validPartNos) {
                updatedQueue[validPartNo]?.resolve(true);
            }
            for (const invalidPartNo of invalidPartNos) {
                updatedQueue[invalidPartNo]?.resolve(false);
            }
            // Remove partNumbers from the queue
            partNumbers.forEach((partNo) => delete updatedQueue[partNo]);
            return updatedQueue;
        });

        // Clear pending items
        setPendingValidations((prev) => prev.filter((partNo) => !partNumbers.includes(partNo)));
    }, [isMounted, pendingValidations, validateQueue]);

    /**
     * Process the availability queue.
     * @type {(function(): Promise<void>)}
     */
    const processAvailabilityQueue = useCallback(async () => {
        const partNumbers = Object.keys(availabilityQueue).filter((partNo) => !pendingAvailabilities.includes(partNo));
        if (partNumbers.length === 0) return;

        // Add these items to pending availabilities
        setPendingAvailabilities((prev) => [...prev, ...partNumbers]);

        const response = await Api.getProductAvailabilitiesV2({partNumbers});

        if (!isMounted()) return;
        if (!response?.resultFlag) {
            // Clear pending items if the response fails
            setPendingAvailabilities((prev) => prev.filter((partNo) => !partNumbers.includes(partNo)));
            return;
        }

        const getPartNoRes = (partNo) => response.data?.find((item) => item.partNumber === partNo)?.localWarehouses ?? [];

        setWarehouseAvailabilities((prev) => ({
            ...prev,
            ...partNumbers.reduce((acc, partNo) => {
                acc[partNo] = getPartNoRes(partNo);
                return acc;
            }, {}),
        }));

        // Resolve queued promises
        setAvailabilityQueue((prevQueue) => {
            const updatedQueue = {...prevQueue};
            for (const partNo of partNumbers) {
                updatedQueue[partNo]?.resolve(getPartNoRes(partNo));
            }
            // Remove partNumbers from the queue
            partNumbers.forEach((partNo) => delete updatedQueue[partNo]);
            return updatedQueue;
        });

        // Clear pending items
        setPendingAvailabilities((prev) =>
            prev.filter((partNo) => !partNumbers.includes(partNo))
        );
    }, [isMounted, pendingAvailabilities, availabilityQueue]);

    /**
     * Process the validation queue and availability queue on mount and on changes.
     */
    useEffect(() => {
        void processValidateQueue();
    }, [processValidateQueue]);

    /**
     * Process the availability queue on mount and on changes.
     */
    useEffect(() => {
        void processAvailabilityQueue();
    }, [processAvailabilityQueue]);

    /**
     * Validate a part number.
     * @type {(function(*): Promise<unknown>)}
     */
    const validatePartNo = useCallback(async partNo => {
        if (validPartNos.includes(partNo)) return true;
        if (invalidPartNos.includes(partNo)) return false;

        // Check if already queued
        if (validateQueue[partNo]) {
            return validateQueue[partNo].promise;
        }

        // Create a deferred promise and add to queue
        const deferred = createDeferred();
        setValidateQueue(prev => ({...prev, [partNo]: deferred}));

        return deferred.promise;
    }, [validPartNos, invalidPartNos, validateQueue]);

    /**
     * Get the availability of a part number.
     * @type {(function(*): Promise<unknown>)}
     */
    const getAvailabilityForPartNo = useCallback(async (partNo, force = false) => {
        if (warehouseAvailabilities[partNo] !== undefined && !force) {
            return warehouseAvailabilities[partNo];
        }

        // Check if already queued
        if (availabilityQueue[partNo]) {
            return availabilityQueue[partNo].promise;
        }

        // Create a deferred promise and add to queue
        const deferred = createDeferred();
        setAvailabilityQueue(prev => ({
            ...prev,
            [partNo]: deferred
        }));

        return deferred.promise;
    }, [warehouseAvailabilities, availabilityQueue]);

    /**
     * Invalidate the validity cache.
     * @type {(function(): void)}
     */
    const invalidateValidityCache = useCallback(() => {
        setValidPartNos([])
        setInvalidPartNos([])
    }, [])

    /**
     * Invalidate the availability cache.
     * @type {(function(): void)}
     */
    const invalidateAvailabilityCache = useCallback(() => {
        setWarehouseAvailabilities({})
    }, [])

    /**
     * Process the price queue.
     * @type {(function(): Promise<void>)}
     */
    const processPriceQueue = useCallback(async () => {
        // Find items that are in the queue but not yet pending
        const queueItems = Object.entries(priceCacheQueue).filter(([key]) => !pendingPrices.includes(key));
        if (queueItems.length === 0) return;

        // Extract keys and add to pending
        const keys = queueItems.map(([key]) => key);
        setPendingPrices((prev) => [...prev, ...keys]);

        // Group by customerId
        const batchRequests = queueItems.reduce((acc, [, {customerId, partNo}]) => {
            if (!acc[customerId]) {
                acc[customerId] = [];
            }
            acc[customerId].push(partNo);
            return acc;
        }, {});

        // Fetch prices for each customer
        const promises = Object.entries(batchRequests).map(async ([customerId, partNumbers]) => {
            const response = await Api.getPriceForCustomer({
                partNumbers,
                customerId
            });

            if (!isMounted()) return;

            // Process response
            if (response?.resultFlag) {
                const newPrices = {};
                // Cache successful results
                response.data?.forEach((item) => {
                    const cacheKey = `${customerId}-${item.partNo}`;
                    newPrices[cacheKey] = item;
                });

                setPriceCache((prev) => ({...prev, ...newPrices}));

                // Resolve promises for this batch
                setPriceCacheQueue((prevQueue) => {
                    const updatedQueue = {...prevQueue};
                    partNumbers.forEach((partNo) => {
                        const cacheKey = `${customerId}-${partNo}`;
                        // If we got a price, resolve with it. If not (but call succeeded), resolve with null/undefined?
                        // The previous logic usually expects an object or null
                        const priceData = newPrices[cacheKey];
                        updatedQueue[cacheKey]?.resolve(priceData);
                        delete updatedQueue[cacheKey];
                    });
                    return updatedQueue;
                });
            } else {
                // Handle failure for this batch - resolve with null or reject?
                // Existing pattern seems to just not resolve or resolve false for validity.
                // Let's resolve with null to indicate failure/no data
                setPriceCacheQueue((prevQueue) => {
                    const updatedQueue = {...prevQueue};
                    partNumbers.forEach((partNo) => {
                        const cacheKey = `${customerId}-${partNo}`;
                        updatedQueue[cacheKey]?.resolve(null);
                        delete updatedQueue[cacheKey];
                    });
                    return updatedQueue;
                });
            }
        });

        await Promise.all(promises);

        // Clear pending items
        setPendingPrices((prev) => prev.filter((key) => !keys.includes(key)));

    }, [isMounted, pendingPrices, priceCacheQueue]);


    /**
     * Process the price queue on mount and on changes.
     */
    useEffect(() => {
        void processPriceQueue();
    }, [processPriceQueue]);


    /**
     * Get the price for a customer and part number.
     * @type {(function(*, *): Promise<unknown>)}
     */
    const getPriceForCustomerAndPartNo = useCallback(async (customerId, partNo) => {
        const key = `${customerId}-${partNo}`;
        if (priceCache[key] !== undefined) {
            return priceCache[key];
        }

        // Check if already queued
        if (priceCacheQueue[key]) {
            return priceCacheQueue[key].promise;
        }

        // Create a deferred promise and add to queue
        const deferred = createDeferred();
        // Store context needed for grouping
        deferred.customerId = customerId;
        deferred.partNo = partNo;

        setPriceCacheQueue(prev => ({
            ...prev,
            [key]: deferred
        }));

        return deferred.promise;
    }, [priceCache, priceCacheQueue]);

    /**
     * Invalidate the price cache.
     * @type {(function(): void)}
     */
    const invalidatePriceCache = useCallback(() => {
        setPriceCache({});
    }, []);

    /**
     * Get all prices for a specific customer from the cache.
     * @type {(function(*): {})}
     */
    const getAllPricesForCustomer = useCallback((customerId) => {
        const prices = {};
        Object.entries(priceCache).forEach(([key, value]) => {
            if (key.startsWith(`${customerId}-`)) {
                if (value?.partNo) {
                    prices[value.partNo] = value;
                }
            }
        });
        return prices;
    }, [priceCache]);

    return {
        validatePartNo,
        getAvailabilityForPartNo,
        invalidateValidityCache: invalidateValidityCache,
        invalidateAvailabilityCache: invalidateAvailabilityCache,
        getPriceForCustomerAndPartNo,
        invalidatePriceCache,
        getAllPricesForCustomer,
    };
}